/**
 * PerformanceChart Component
 *
 * Multi-line Victory chart comparing all three AI portfolios over time.
 * Includes period selector (1W, 1M, 3M, YTD, ALL) and colored legend.
 */

import { FC, useState, useMemo, useEffect } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { VictoryChart, VictoryLine, VictoryAxis, VictoryCursorContainer, LineSegment } from 'victory';
import { colors, spacing, fontStyles } from '../../styles';
import {
  getInvestingArenaChartData,
  AIModelId,
  AI_MODEL_NAMES,
  AI_MODEL_COLORS,
  AI_MODEL_ICONS,
} from '../../apis/investingArena';

type ChartPeriod = '1W' | '1M' | '3M' | 'YTD' | 'ALL';

const PERIODS: ChartPeriod[] = ['1W', '1M', '3M', 'YTD', 'ALL'];
const AI_MODEL_ORDER: AIModelId[] = ['openai', 'gemini', 'claude'];

const roundWithoutNegativeZero = (value: number, decimals: number): number => {
  const rounded = Number(value.toFixed(decimals));
  return Object.is(rounded, -0) ? 0 : rounded;
};

export const formatPercentageLabel = (value: number, decimals = 0): string => {
  const rounded = roundWithoutNegativeZero(value, decimals);
  return `${rounded.toFixed(decimals)}%`;
};

export const formatSignedPercentageLabel = (value: number, decimals = 1): string => {
  const rounded = roundWithoutNegativeZero(value, decimals);
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded.toFixed(decimals)}%`;
};

const skeletonPulse = css`
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  animation: pulse 1.5s ease-in-out infinite;
`;

// Skeleton loader for the chart
export const PerformanceChartSkeleton: FC = () => {
  return (
    <div
      css={css`
        padding: ${spacing.deci};
        border-radius: 12px;
        background: var(--card-background);
      `}
    >
      {/* Period selector skeleton */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: ${spacing.deci};
        `}
      >
        <div
          css={css`
            width: 140px;
            height: 20px;
            border-radius: 4px;
            background: ${colors.cloud};
            ${skeletonPulse}
            html[data-theme='dark'] & {
              background: ${colors.tungsten};
            }
          `}
        />
        <div
          css={css`
            display: flex;
            gap: ${spacing.centi};
          `}
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              css={css`
                width: 32px;
                height: 28px;
                border-radius: 4px;
                background: ${colors.cloud};
                ${skeletonPulse}
                html[data-theme='dark'] & {
                  background: ${colors.tungsten};
                }
              `}
            />
          ))}
        </div>
      </div>
      {/* Chart skeleton */}
      <div
        css={css`
          height: 200px;
          border-radius: 8px;
          background: ${colors.cloud};
          ${skeletonPulse}
          html[data-theme='dark'] & {
            background: ${colors.tungsten};
          }
        `}
      />
      {/* Legend skeleton */}
      <div
        css={css`
          display: flex;
          justify-content: center;
          gap: ${spacing.deci};
          margin-top: ${spacing.deci};
        `}
      >
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            css={css`
              width: 80px;
              height: 16px;
              border-radius: 4px;
              background: ${colors.cloud};
              ${skeletonPulse}
              html[data-theme='dark'] & {
                background: ${colors.tungsten};
              }
            `}
          />
        ))}
      </div>
    </div>
  );
};

interface PerformanceChartProps {
  initialPeriod?: ChartPeriod;
}

export const PerformanceChart: FC<PerformanceChartProps> = ({ initialPeriod = '1M' }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<ChartPeriod>(initialPeriod);
  const [cursorValue, setCursorValue] = useState<number | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => document.documentElement.getAttribute('data-theme') === 'dark');

  // Watch for theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const { data: chartData, isLoading } = useQuery(
    ['investing-arena', 'chart', period],
    () => getInvestingArenaChartData(period),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
    },
  );

  // Transformed data point type
  type TransformedDataPoint = { x: number; y: number; date: string };
  type TransformedData = Record<AIModelId, TransformedDataPoint[]>;

  // Transform data for Victory chart
  const transformedData = useMemo((): TransformedData => {
    const emptyResult: TransformedData = {
      claude: [],
      openai: [],
      gemini: [],
    };

    if (!chartData) return emptyResult;

    AI_MODEL_ORDER.forEach((modelId) => {
      const modelData = chartData[modelId] || [];
      emptyResult[modelId] = modelData.map((point, index) => ({
        x: index,
        y: point.value,
        date: new Date(point.time * 1000).toISOString().slice(0, 10),
      }));
    });

    return emptyResult;
  }, [chartData]);

  // Calculate min/max values for y-axis
  const { minY, maxY, dataLength } = useMemo(() => {
    const allValues: number[] = [];
    let maxLen = 0;

    Object.values(transformedData).forEach((data) => {
      if (data && data.length > 0) {
        data.forEach((point) => allValues.push(point.y));
        maxLen = Math.max(maxLen, data.length);
      }
    });

    if (allValues.length === 0) {
      return { minY: 0, maxY: 10, dataLength: 0 };
    }

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const padding = (max - min) * 0.1 || 5;

    return {
      minY: min - padding,
      maxY: max + padding,
      dataLength: maxLen,
    };
  }, [transformedData]);

  // Get current values for each model at cursor position
  const currentValues = useMemo(() => {
    const result: Record<AIModelId, { value: number; date: string } | null> = {
      claude: null,
      openai: null,
      gemini: null,
    };

    AI_MODEL_ORDER.forEach((modelId) => {
      const data = transformedData[modelId];
      if (data && data.length > 0) {
        const index = cursorValue !== null ? Math.round(cursorValue) : data.length - 1;
        const point = data[Math.min(index, data.length - 1)];
        if (point) {
          result[modelId] = { value: point.y, date: point.date };
        }
      }
    });

    return result;
  }, [transformedData, cursorValue]);

  if (isLoading) {
    return <PerformanceChartSkeleton />;
  }

  const hasData = Object.values(transformedData).some((data) => data && data.length > 0);

  if (!hasData) {
    return (
      <div
        css={css`
          padding: ${spacing.deci};
          border-radius: 12px;
          background: var(--card-background);
          text-align: center;
          ${fontStyles.cat}
          color: ${colors.silver};
        `}
      >
        {t('aiarena.noChartData')}
      </div>
    );
  }

  return (
    <div
      css={css`
        padding: ${spacing.deci};
        border-radius: 8px;
        background: ${colors.snow};
        border: 0.5px solid ${colors.cloud};

        html[data-theme='dark'] & {
          background: var(--background-color);
          border-color: ${colors.tungsten};
        }
      `}
    >
      {/* Header with period selector */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: ${spacing.centi};
        `}
      >
        <h3
          css={css`
            ${fontStyles.lion}
            margin: 0;
            color: var(--text-color);
          `}
        >
          {t('aiarena.performanceHistory')}
        </h3>
        <div
          css={css`
            display: flex;
            gap: ${spacing.milli};
          `}
        >
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              css={css`
                padding: ${spacing.milli} ${spacing.centi};
                border-radius: 4px;
                border: 1px solid ${period === p ? colors.petrolBlue : 'var(--border-color)'};
                background: ${period === p ? colors.petrolBlue : 'transparent'};
                color: ${period === p ? colors.sugar : 'var(--text-color)'};
                cursor: pointer;
                ${fontStyles.flea}
                font-weight: 500;
                transition: all 0.15s ease;

                &:hover {
                  border-color: ${colors.petrolBlue};
                }

                html[data-theme='dark'] & {
                  background: ${period === p ? colors.skyBlue : 'transparent'};
                  color: ${period === p ? colors.liquorice : 'var(--text-color)'};
                  border-color: ${period === p ? colors.skyBlue : 'var(--border-color)'};
                }
              `}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div
        css={css`
          height: 200px;
          margin: 0 -${spacing.centi};
        `}
      >
        <VictoryChart
          padding={{ top: 10, bottom: 30, left: 50, right: 20 }}
          height={200}
          domain={{
            x: [0, dataLength - 1],
            y: [minY, maxY],
          }}
          containerComponent={
            <VictoryCursorContainer
              cursorDimension="x"
              onCursorChange={(value: any) => setCursorValue(typeof value === 'number' ? value : null)}
              cursorComponent={<LineSegment style={{ stroke: cursorValue ? colors.cloud : 'transparent' }} />}
              style={{ touchAction: 'auto' }}
            />
          }
        >
          {/* Y-axis */}
          <VictoryAxis
            dependentAxis
            tickFormat={(t) => formatPercentageLabel(t, 0)}
            style={{
              axis: { stroke: 'transparent' },
              grid: { stroke: isDarkMode ? colors.tungsten : colors.cloud, strokeDasharray: '4,4', opacity: 0.5 },
              tickLabels: {
                fill: isDarkMode ? colors.cloud : colors.silver,
                fontSize: 10,
                fontFamily: 'HelveticaNowText, sans-serif',
              },
            }}
          />

          {/* X-axis with dates */}
          <VictoryAxis
            tickFormat={(x) => {
              // Find first model with data (empty arrays are truthy, so check length)
              const data =
                (transformedData.openai.length > 0 && transformedData.openai) ||
                (transformedData.claude.length > 0 && transformedData.claude) ||
                (transformedData.gemini.length > 0 && transformedData.gemini);
              const index = Math.round(x);
              if (!data || !data[index] || !data[index].date) return '';
              try {
                const date = new Date(data[index].date + 'T00:00:00');
                if (isNaN(date.getTime())) return '';
                return format(date, 'MMM d');
              } catch {
                return '';
              }
            }}
            tickCount={4}
            style={{
              axis: { stroke: isDarkMode ? colors.tungsten : colors.cloud },
              tickLabels: {
                fill: isDarkMode ? colors.cloud : colors.silver,
                fontSize: 10,
                fontFamily: 'HelveticaNowText, sans-serif',
              },
            }}
          />

          {/* Lines for each AI model */}
          {AI_MODEL_ORDER.map((modelId) => {
            const data = transformedData[modelId];
            if (!data || data.length === 0) return null;

            return (
              <VictoryLine
                key={modelId}
                data={data}
                interpolation="monotoneX"
                style={{
                  data: {
                    stroke: AI_MODEL_COLORS[modelId],
                    strokeWidth: 2,
                  },
                }}
              />
            );
          })}
        </VictoryChart>
      </div>

      {/* Legend with current values */}
      <div
        css={css`
          display: flex;
          justify-content: center;
          gap: ${spacing.deci};
          margin-top: ${spacing.centi};
        `}
      >
        {AI_MODEL_ORDER.map((modelId) => {
          const current = currentValues[modelId];
          const modelIcon = AI_MODEL_ICONS[modelId];
          const roundedCurrentValue = current ? roundWithoutNegativeZero(current.value, 1) : null;

          return (
            <div
              key={modelId}
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.milli};
              `}
            >
              <div
                css={css`
                  width: 20px;
                  height: 20px;
                  border-radius: 50%;
                  background: white;
                  border: 1px solid ${colors.cloud};
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  html[data-theme='dark'] & {
                    background: #2a2a2a;
                    border-color: ${colors.tungsten};
                  }
                `}
              >
                {modelIcon && (
                  <img
                    src={modelIcon}
                    alt=""
                    css={css`
                      width: 12px;
                      height: 12px;

                      html[data-theme='dark'] & {
                        filter: ${modelId === 'openai' ? 'invert(1)' : 'none'};
                      }
                    `}
                  />
                )}
              </div>
              <div>
                <span
                  css={css`
                    ${fontStyles.flea}
                    color: ${AI_MODEL_COLORS[modelId]};
                    font-weight: 500;

                    html[data-theme='dark'] & {
                      color: ${modelId === 'openai' ? colors.snow : AI_MODEL_COLORS[modelId]};
                    }
                  `}
                >
                  {AI_MODEL_NAMES[modelId]}
                </span>
                {current && roundedCurrentValue !== null && (
                  <span
                    css={css`
                      ${fontStyles.flea}
                      color: ${roundedCurrentValue >= 0 ? colors.leaf : colors.berry};
                      margin-left: ${spacing.milli};
                    `}
                  >
                    {formatSignedPercentageLabel(current.value, 1)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PerformanceChart;
